import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { registerLicense } from '@syncfusion/ej2-base';
import * as Sentry from '@sentry/react';

import App from './App';
import { store } from './store';
import { theme } from './styles/theme';

import './styles/global.css';

// Initialize Sentry (MUST be before ReactDOM.render)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // Capture 100% of transactions in dev, 10% in prod
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Enable Sentry in production only
    enabled: import.meta.env.PROD,
    // Automatically capture unhandled errors
    beforeSend(event) {
      return event;
    },
  });
}

// Register Syncfusion license (MUST be before ReactDOM.render)
// Get free trial key: https://www.syncfusion.com/account/claim-license-key
if (import.meta.env.VITE_SYNCFUSION_LICENSE_KEY) {
  registerLicense(import.meta.env.VITE_SYNCFUSION_LICENSE_KEY);
}

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// Error boundary for development
if (import.meta.env.DEV) {
  // Add global error handlers for development
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

// Create Sentry-wrapped BrowserRouter for automatic performance monitoring
const SentryRoutes = Sentry.withSentryRouting(BrowserRouter);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1>Something went wrong</h1>
          <p>We have recorded this issue and will fix it as soon as possible.</p>
          <details style={{ marginTop: '1rem' }}>
            <summary>Error details</summary>
            <pre style={{ textAlign: 'left', background: '#f5f5f5', padding: '1rem' }}>
              {error instanceof Error ? error.toString() : String(error)}
            </pre>
          </details>
          <button
            onClick={resetError}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}
      showDialog
    >
      <Provider store={store}>
        <SentryRoutes>
          <ConfigProvider theme={theme}>
            <App />
          </ConfigProvider>
        </SentryRoutes>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
); 